import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  GraduationCap,
  Star,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { type Skill } from '@/lib/mock-skills-data';
import { fetchSkills, type SkillSummaryResponse } from '@/lib/data-service';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortField = 'skillName' | 'category' | 'resourceCount';
type SortDir = 'asc' | 'desc';

// ─── Helper: map API response to the Skill shape used by the UI ──────────────

function mapApiSkillToUiSkill(s: SkillSummaryResponse): Skill {
  return {
    skillId: String(s.id),
    skillName: s.skillName,
    category: s.category,
    resourceCount: s.resourceCount,
    resources: s.resources.map(r => ({
      personId: String(r.personId),
      personName: r.personName,
      role: r.role,
      proficiency: r.proficiency as 1 | 2 | 3 | 4 | 5,
      photo: r.photo,
    })),
  };
}

const CATEGORIES = ['All', 'Technical', 'Design', 'Delivery', 'Domain / Sector', 'Leadership'];

const CATEGORY_BADGE: Record<string, string> = {
  Technical: 'bg-blue-50 text-blue-700 border-blue-200',
  Design: 'bg-purple-50 text-purple-700 border-purple-200',
  Delivery: 'bg-teal-50 text-teal-700 border-teal-200',
  'Domain / Sector': 'bg-warning-50 text-warning-700 border-warning-200',
  Leadership: 'bg-rose-50 text-rose-700 border-rose-200',
};

// ─── Star Rating ──────────────────────────────────────────────────────────────

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`h-3.5 w-3.5 ${
          star <= rating
            ? 'fill-warning-400 text-warning-400'
            : 'fill-neutral-200 text-neutral-200'
        }`}
      />
    ))}
  </div>
);

// ─── Resources Drawer ─────────────────────────────────────────────────────────

const ResourcesDrawer: React.FC<{
  skill: Skill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewInDirectory: (skillName: string) => void;
}> = ({ skill, open, onOpenChange, onViewInDirectory }) => {
  if (!skill) return null;

  const avgProficiency =
    skill.resources.reduce((sum, r) => sum + r.proficiency, 0) / skill.resources.length;

  const sorted = [...skill.resources].sort((a, b) => b.proficiency - a.proficiency);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col h-full">
        {/* Header */}
        <div className="border-b bg-background px-6 py-5 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <SheetTitle className="text-xl font-bold">{skill.skillName}</SheetTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_BADGE[skill.category] ?? ''}`}
                >
                  {skill.category}
                </span>
                <span className="text-sm text-muted-foreground">
                  {skill.resourceCount} {skill.resourceCount === 1 ? 'resource' : 'resources'}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Average proficiency */}
          <div className="mt-4 flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Star className="h-4 w-4 text-warning-500 fill-warning-400 flex-shrink-0" />
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Avg. proficiency</span>
              <StarRating rating={Math.round(avgProficiency)} />
              <span className="text-sm font-semibold text-foreground">
                {avgProficiency.toFixed(1)} / 5
              </span>
            </div>
          </div>
        </div>

        {/* Resource list */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {sorted.map((resource, idx) => (
              <React.Fragment key={resource.personId}>
                <div className="flex items-center justify-between py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9 rounded-md flex-shrink-0">
                      <AvatarImage src={resource.photo} alt={resource.personName} />
                      <AvatarFallback className="text-xs bg-teal-100 text-teal-700 rounded-md">
                        {resource.personName.split(' ').map((n) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {resource.personName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{resource.role}</p>
                    </div>
                  </div>
                  <StarRating rating={resource.proficiency} />
                </div>
                {idx < sorted.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-background px-6 py-4 flex-shrink-0">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              onViewInDirectory(skill.skillName);
            }}
          >
            <Users className="h-4 w-4 mr-2" />
            View all in Directory
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ─── Sort Icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ field: SortField; activeField: SortField; dir: SortDir }> = ({
  field,
  activeField,
  dir,
}) => {
  if (field !== activeField) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40 ml-1" />;
  return dir === 'asc' ? (
    <ArrowUp className="h-3.5 w-3.5 ml-1 text-primary" />
  ) : (
    <ArrowDown className="h-3.5 w-3.5 ml-1 text-primary" />
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export function SkillsPage() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortField, setSortField] = useState<SortField>('skillName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);

  // Data fetching state
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSkills();
      setSkills(data.map(mapApiSkillToUiSkill));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return skills.filter((skill) => {
      const matchesCategory =
        categoryFilter === 'All' || skill.category === categoryFilter;
      const matchesSearch =
        !q ||
        skill.skillName.toLowerCase().includes(q) ||
        skill.category.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    }).sort((a, b) => {
      let cmp = 0;
      if (sortField === 'skillName') cmp = a.skillName.localeCompare(b.skillName);
      else if (sortField === 'category') cmp = a.category.localeCompare(b.category);
      else if (sortField === 'resourceCount') cmp = a.resourceCount - b.resourceCount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [skills, searchQuery, categoryFilter, sortField, sortDir]);

  const handleRowClick = (skill: Skill) => {
    setSelectedSkill(skill);
    setDrawerOpen(true);
  };

  const handleViewInDirectory = (skillName: string) => {
    navigate(`/directory?skill=${encodeURIComponent(skillName)}`);
  };

  const hasActiveFilters = searchQuery !== '' || categoryFilter !== 'All';

  // Stats
  const totalCategories = new Set(skills.map((s) => s.category)).size;
  const totalResources = useMemo(() => {
    const ids = new Set<string>();
    skills.forEach((s) => s.resources.forEach((r) => ids.add(r.personId)));
    return ids.size;
  }, [skills]);

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Skills"
        subtitle="Skills defined in Resource Manager. Counts show active resources synced to Projects, so totals may differ from Resource Manager. Read-only."
        size={20}
        showActions={false}
      />

      <Card>
        {/* Loading state */}
        {loading && (
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading skills…</span>
            </div>
          </CardContent>
        )}

        {/* Error state */}
        {error && !loading && (
          <CardContent className="pt-6">
            <div className="text-center py-16">
              <GraduationCap className="h-10 w-10 text-destructive/30 mx-auto mb-3" />
              <p className="text-destructive font-medium">Failed to load skills</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              <Button variant="outline" className="mt-4" onClick={loadSkills}>
                Try again
              </Button>
            </div>
          </CardContent>
        )}

        {!loading && !error && (
          <>
            {/* Filters row */}
            <CardContent className="pt-6 pb-4">
              <div className="flex gap-3 flex-wrap" data-tour="skills-search">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search skills or categories…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat === 'All' ? 'All Categories' : cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setCategoryFilter('All');
                    }}
                    className="text-muted-foreground"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>

            {/* Table — fills card edge-to-edge */}
            <CardContent className="p-0">
              <Table data-tour="skills-table">
                <TableHeader divided className="border-t">
                  <TableRow className="hover:bg-transparent">
                    <TableHead>
                      <button
                        onClick={() => handleSort('skillName')}
                        className="flex items-center font-semibold hover:text-foreground transition-colors"
                      >
                        Skill
                        <SortIcon field="skillName" activeField={sortField} dir={sortDir} />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('category')}
                        className="flex items-center font-semibold hover:text-foreground transition-colors"
                      >
                        Category
                        <SortIcon field="category" activeField={sortField} dir={sortDir} />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('resourceCount')}
                        className="flex items-center font-semibold hover:text-foreground transition-colors"
                      >
                        <Users className="h-3.5 w-3.5 mr-1.5" />
                        Resources
                        <SortIcon field="resourceCount" activeField={sortField} dir={sortDir} />
                      </button>
                    </TableHead>
                    <TableHead>Top Resources</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-16">
                        <GraduationCap className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground font-medium">No skills found</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          Try adjusting your search or filter
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAndSorted.map((skill) => {
                      const previewResources = skill.resources.slice(0, 4);
                      const overflow = skill.resources.length - 4;
                      return (
                        <TableRow
                          key={skill.skillId}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => handleRowClick(skill)}
                        >
                          {/* Skill name */}
                          <TableCell className="font-medium">{skill.skillName}</TableCell>

                          {/* Category badge */}
                          <TableCell>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_BADGE[skill.category] ?? 'bg-neutral-50 text-neutral-700 border-neutral-200'}`}
                            >
                              {skill.category}
                            </span>
                          </TableCell>

                          {/* Resource count */}
                          <TableCell>
                            <span className="font-semibold text-foreground">
                              {skill.resourceCount}
                            </span>
                          </TableCell>

                          {/* Avatar stack + names */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex -space-x-2">
                                {previewResources.map((r) => (
                                  <Avatar
                                    key={r.personId}
                                    className="h-6 w-6 border-2 border-background rounded-full"
                                  >
                                    <AvatarImage src={r.photo} alt={r.personName} />
                                    <AvatarFallback className="text-[10px] bg-teal-100 text-teal-700">
                                      {r.personName
                                        .split(' ')
                                        .map((n) => n[0])
                                        .join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {overflow > 0 && (
                                  <div className="h-6 w-6 rounded-full border-2 border-background bg-muted flex items-center justify-center">
                                    <span className="text-[10px] font-medium text-muted-foreground">
                                      +{overflow}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {previewResources
                                  .slice(0, 2)
                                  .map((r) => r.personName.split(' ')[0])
                                  .join(', ')}
                                {skill.resources.length > 2 &&
                                  ` (+${skill.resources.length - 2})`}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>

            {/* Footer count */}
            {filteredAndSorted.length > 0 && (
              <CardContent className="px-6 py-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-3">
                  <span>
                    Showing {filteredAndSorted.length} of {skills.length} skills
                  </span>
                  <div className="flex items-center gap-4">
                    <span>{totalCategories} categories</span>
                    <span>·</span>
                    <span>{totalResources} resources</span>
                  </div>
                </div>
              </CardContent>
            )}
          </>
        )}
      </Card>

      {/* Resources side drawer */}
      <ResourcesDrawer
        skill={selectedSkill}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onViewInDirectory={handleViewInDirectory}
      />
    </div>
  );
}
